<template>
  <div class="segment-publish-assistant">
    <div class="assistant-header">
      <h3>发布助手</h3>
      <p>导出发布素材包，复制文案，勾选各平台发布进度（保存在本机浏览器）。</p>
    </div>

    <el-form label-width="88px" size="small" class="meta-form">
      <el-form-item label="话题标签">
        <el-input
          v-model="localHashtags"
          placeholder="#电影解说 #影视剪辑"
          maxlength="512"
          @blur="persistMeta"
        />
      </el-form-item>
      <el-form-item label="简介模板">
        <el-input
          v-model="localDescriptionTemplate"
          type="textarea"
          :rows="2"
          placeholder="第{index}集：{title}"
          maxlength="1024"
          @blur="persistMeta"
        />
      </el-form-item>
    </el-form>

    <div class="assistant-actions">
      <el-button
        type="primary"
        icon="el-icon-download"
        :disabled="!visibleClips.length || downloading"
        :loading="downloading"
        @click="handleDownloadPack"
      >
        下载发布素材包 ({{ visibleClips.length }})
      </el-button>
      <el-button
        icon="el-icon-document-copy"
        :disabled="!visibleClips.length"
        @click="handleCopyAll"
      >
        复制全部文案
      </el-button>
      <el-checkbox v-model="onlyUnpublished" class="filter-checkbox">
        仅看未发布
      </el-checkbox>
    </div>

    <el-table :data="visibleClips" size="small" border class="clip-table">
      <el-table-column label="#" width="48" prop="index" />
      <el-table-column label="标题" min-width="140">
        <template slot-scope="{ row }">
          {{ segmentTitle(row.fileName) }}
        </template>
      </el-table-column>
      <el-table-column
        v-for="platform in platforms"
        :key="platform.id"
        :label="platform.name"
        width="72"
        align="center"
      >
        <template slot-scope="{ row }">
          <el-checkbox
            :value="isChecked(row, platform.id)"
            @change="val => toggleCheck(row, platform.id, val)"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="96" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" size="mini" @click="handleCopyOne(row)">复制</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-collapse class="platform-guides">
      <el-collapse-item title="各平台上传指引" name="guides">
        <div v-for="platform in platforms" :key="platform.id" class="guide-item">
          <strong>{{ platform.name }}</strong>
          <span>{{ platform.ratio }} · 封面 {{ platform.coverSize }} · {{ platform.durationHint }}</span>
          <a :href="platform.portalUrl" target="_blank" rel="noopener noreferrer">
            {{ platform.portalLabel }}
          </a>
        </div>
        <p class="mobile-tip">{{ mobileTip }}</p>
      </el-collapse-item>
    </el-collapse>

    <el-dialog
      :visible.sync="packDialogVisible"
      title="正在打包发布素材"
      width="440px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      append-to-body
      custom-class="publish-pack-dialog"
    >
      <div class="pack-dialog-body">
        <i class="el-icon-loading pack-dialog-icon"></i>
        <p class="pack-dialog-text">{{ packDialogText }}</p>
        <p class="pack-dialog-sub">打包完成后浏览器将自动开始下载，请勿关闭本页。</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { PUBLISH_PLATFORMS, MOBILE_TRANSFER_TIP } from '../constants/publishPlatforms';
import {
  buildBatchMarkdown,
  buildSegmentCopyText,
  copyText,
  segmentTitle,
} from '../utils/publishCopy';
import {
  clipChecklistKey,
  isClipUnpublished,
  loadPublishChecklist,
  savePublishChecklist,
  togglePlatform,
} from '../utils/publishChecklist';
import { downloadPublishPack } from '../utils/download';
import { updatePublishMeta } from '../api/segment';

export default {
  name: 'SegmentPublishAssistant',
  props: {
    taskId: { type: String, default: null },
    clips: { type: Array, default: () => [] },
    selectedClips: { type: Array, default: () => [] },
    coverTitle: { type: String, default: '' },
    publishHashtags: { type: String, default: '' },
    publishDescriptionTemplate: { type: String, default: '' },
    useMock: { type: Boolean, default: false },
    fallbackFileName: { type: String, default: 'publish_pack.zip' },
  },
  data() {
    return {
      platforms: PUBLISH_PLATFORMS,
      mobileTip: MOBILE_TRANSFER_TIP,
      localHashtags: this.publishHashtags || '',
      localDescriptionTemplate: this.publishDescriptionTemplate || '',
      checklist: {},
      onlyUnpublished: false,
      downloading: false,
      packDialogVisible: false,
      packDialogText: '',
    };
  },
  computed: {
    platformIds() {
      return this.platforms.map(item => item.id);
    },
    packClips() {
      return this.selectedClips.length ? this.selectedClips : this.clips;
    },
    visibleClips() {
      const base = this.packClips;
      if (!this.onlyUnpublished) {
        return base;
      }
      return base.filter(clip => isClipUnpublished(
        this.checklist,
        clipChecklistKey(clip),
        this.platformIds,
      ));
    },
    publishMeta() {
      return {
        hashtags: this.localHashtags,
        descriptionTemplate: this.localDescriptionTemplate,
        seriesTitle: this.coverTitle || '',
      };
    },
  },
  watch: {
    taskId: {
      immediate: true,
      handler(value) {
        this.checklist = loadPublishChecklist(value);
      },
    },
    publishHashtags(value) {
      this.localHashtags = value || '';
    },
    publishDescriptionTemplate(value) {
      this.localDescriptionTemplate = value || '';
    },
  },
  methods: {
    segmentTitle,
    isChecked(clip, platformId) {
      const key = clipChecklistKey(clip);
      return !!(this.checklist[key] && this.checklist[key][platformId]);
    },
    toggleCheck(clip, platformId, checked) {
      const key = clipChecklistKey(clip);
      this.checklist = togglePlatform(this.checklist, key, platformId, checked);
      savePublishChecklist(this.taskId, this.checklist);
    },
    async persistMeta() {
      this.$emit('update:publishHashtags', this.localHashtags);
      this.$emit('update:publishDescriptionTemplate', this.localDescriptionTemplate);
      if (this.useMock || !this.taskId) {
        return;
      }
      try {
        await updatePublishMeta(this.taskId, {
          hashtags: this.localHashtags,
          descriptionTemplate: this.localDescriptionTemplate,
        });
      } catch (e) {
        this.$message.error(e.message || '保存发布文案失败');
      }
    },
    async handleCopyOne(clip) {
      try {
        await copyText(buildSegmentCopyText(clip, this.publishMeta));
        this.$message.success('已复制文案');
      } catch (e) {
        this.$message.error(e.message || '复制失败');
      }
    },
    async handleCopyAll() {
      try {
        await copyText(buildBatchMarkdown(this.visibleClips, this.publishMeta));
        this.$message.success('已复制全部文案');
      } catch (e) {
        this.$message.error(e.message || '复制失败');
      }
    },
    async handleDownloadPack() {
      if (this.useMock || !this.taskId) {
        this.$message.warning('Mock 模式下无法下载发布包，请联调 Java 后端');
        return;
      }
      const count = this.visibleClips.length;
      this.downloading = true;
      this.packDialogText = count > 1
        ? `正在打包 ${count} 条发布素材（含视频与封面），文件较多时可能需要等待片刻…`
        : '正在打包发布素材，请稍候…';
      this.packDialogVisible = true;
      try {
        await this.persistMeta();
        const segmentIds = this.visibleClips.map(c => c.id).filter(Boolean);
        if (count > 3) {
          this.packDialogText = `正在打包 ${count} 条素材，服务器正在压缩视频文件，请耐心等待…`;
        }
        await downloadPublishPack(this.taskId, segmentIds, this.fallbackFileName);
        this.$message.success(`已开始下载发布素材包（${count} 条）`);
      } catch (e) {
        this.$message.error(e.message || '下载发布包失败');
      } finally {
        this.packDialogVisible = false;
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.segment-publish-assistant {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f8fbff;
}
.assistant-header h3 {
  margin: 0 0 4px;
  font-size: 16px;
  color: #111827;
}
.assistant-header p {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
}
.meta-form {
  margin-bottom: 8px;
}
.assistant-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.filter-checkbox {
  margin-left: 4px;
}
.clip-table {
  margin-bottom: 12px;
}
.platform-guides {
  background: #fff;
  border: none;
}
.guide-item {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #374151;
}
.guide-item a {
  color: #2563eb;
}
.mobile-tip {
  margin: 8px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
</style>

<style>
.publish-pack-dialog .el-dialog__body {
  padding-top: 8px;
  padding-bottom: 24px;
}
.publish-pack-dialog .pack-dialog-body {
  text-align: center;
  padding: 8px 4px 0;
}
.publish-pack-dialog .pack-dialog-icon {
  font-size: 36px;
  color: #409eff;
}
.publish-pack-dialog .pack-dialog-text {
  margin: 16px 0 8px;
  font-size: 15px;
  line-height: 1.6;
  color: #111827;
}
.publish-pack-dialog .pack-dialog-sub {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}
</style>
